import { env } from '~/env'
import { Suspense, lazy } from 'react'
import {
  createBrowserRouter,
  Route,
  createRoutesFromElements,
  RouterProvider
} from 'react-router-dom'
import { withDevCycleProvider } from '@devcycle/react-client-sdk'
import { useDevcycle } from '~/hooks/useDevcycle'
import { getUser } from '~/utilities/getUserFromLS'

import AcceptInvite from './Auth/AcceptInvite'
import Error404 from './Error404'
import Layout from './Layout'
import Login from './Auth/Login'
import OrgRedirect from './Org'
import OrgSettings from './Org/OrgSettings'
import Pricing from './Org/Pricing'
import Projects from './Org/Projects/Projects'
import ProtectedRoute from 'components/ProtectedRoute'
import Register from './Auth/Register'
import Reviews from './Org/Projects/Reviews'
import Signup from './Auth/Login/Signup'
import Loading from '~/components/ui/Loading'
import ProcoreLogin from './Procore/Login'
import Callback from './Procore/Callback'
import Procore from './Procore'
import Submittals from './Org/Projects/Submittals'
import WorkFlow from './Org/Projects/Submittals/WorkFlow'
import ScheduleSelection from './Org/Projects/Submittals/WorkFlow/ScheduleSelection'
import ConfirmSpecifiedProducts from './Org/Projects/Submittals/WorkFlow/ConfirmSpecifiedProducts'
import ConfirmProductRequirements from './Org/Projects/Submittals/WorkFlow/ConfirmProductRequirements'
import ConfirmMatchedSpecifiedProducts from '~/app/Org/Projects/Submittals/WorkFlow/ConfirmMatchedSpecifiedProducts'
import FreeTierEndedDialog from '~/components/FreeTierEndedDialog'
import SmartMatch from './Org/Projects/Reviews/SmartMatch'
import { Security, LoginCallback as OktaLoginCallback } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import OktaLogin from './Auth/OktaLogin'
import { useOktaLogin } from '~/hooks/useOktaLogin'
import { useAppLoadingStore } from '~/store/appLoadingStore'
const Onboarding = lazy(() => import('./Onboarding'))
const Viewer = lazy(() => import('./Org/Projects/Viewer'))
const SinglePageViewer = lazy(() => import('./Org/Projects/Viewer/SinglePageViewer'))
const Jobs = lazy(() => import('./Admin/Jobs'))
const QuotaReport = lazy(() => import('./Admin/QuotaReport'))
const UsageReport = lazy(() => import('./Org/OrgSettings/UsageReport/UsageReport'))
const ProjectAssignment = lazy(
  () => import('./Org/OrgSettings/ProjectAssignment/ProjectAssignment')
)

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/procore/login" element={<ProcoreLogin />} />
      <Route path="/procore/callback" element={<Callback />} />
      <Route path="/okta/login" element={<OktaLogin />} />
      <Route path="/okta/login/callback" element={<OktaLoginCallback />} />
      <Route path="/invite-link" element={<AcceptInvite />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route path="/signup" element={<Signup />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Login />} />

      <Route path="/" element={<Layout />}>
        {/* Submittals */}

        <Route
          path="/org/:orgId/project/:projectId/submittals/workflow/:workflowInstanceId/confirm-matched-products"
          element={
            <ProtectedRoute>
              <ConfirmMatchedSpecifiedProducts />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/project/:projectId/submittals/workflow/:workflowInstanceId/confirm-product-requirements"
          element={
            <ProtectedRoute>
              <ConfirmProductRequirements />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/project/:projectId/submittals/workflow/:workflowInstanceId/confirm-products"
          element={
            <ProtectedRoute>
              <ConfirmSpecifiedProducts />
            </ProtectedRoute>
          }
        />

        {/* Submittals */}
        <Route
          path="/org/:orgId/project/:projectId/submittals/workflow/:workflowInstanceId/schedule-selection"
          element={
            <ProtectedRoute>
              <ScheduleSelection />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/project/:projectId/submittals/workflow/:workflowInstanceId"
          element={
            <ProtectedRoute>
              <WorkFlow />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/project/:projectId/submittals"
          element={
            <ProtectedRoute>
              <Submittals />
            </ProtectedRoute>
          }
        />

        {/* Reviews */}
        <Route
          path="/org/:orgId/project/:projectId/reviews"
          element={
            <ProtectedRoute>
              <Reviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="procore/org/:orgId/project/:projectId/reviews"
          element={
            <ProtectedRoute>
              <Reviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="procore/org/:orgId/project/:projectId/reviews/:reviewId"
          element={
            <ProtectedRoute>
              <Reviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:orgId/project/:projectId/reviews/:reviewId"
          element={
            <ProtectedRoute>
              <Reviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:orgId/project/:projectId/reviews/:reviewId/view"
          element={
            <ProtectedRoute>
              <Viewer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:orgId/project/:projectId/reviews/:reviewId/smart-match"
          element={
            <ProtectedRoute>
              <SmartMatch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:orgId/project/:projectId/reviews/:reviewId/single-page-viewer"
          element={
            <ProtectedRoute>
              <SinglePageViewer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/projects"
          element={
            <ProtectedRoute>
              <Projects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:orgId/pricing"
          element={
            <ProtectedRoute>
              <Pricing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/org/:orgId/settings"
          element={
            <ProtectedRoute>
              <OrgSettings />
            </ProtectedRoute>
          }
        />

        <Route path="/org" element={<OrgRedirect />} />

        <Route
          path="/admin/jobs/:projectId/:reviewId"
          element={
            <ProtectedRoute admin={true}>
              <Jobs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/jobs/:projectId/:reviewId/smart-match"
          element={
            <ProtectedRoute admin={true}>
              <SmartMatch />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/jobs"
          element={
            <ProtectedRoute admin={true}>
              <Jobs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/usage-report"
          element={
            <ProtectedRoute>
              <UsageReport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/org/:orgId/project-assignment"
          element={
            <ProtectedRoute>
              <ProjectAssignment />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/usage-report"
          element={
            <ProtectedRoute admin={true}>
              <QuotaReport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/procore"
          element={
            <ProtectedRoute>
              <Procore />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Error404 />
          </ProtectedRoute>
        }
      />
    </>
  )
)

const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
  window.location.assign(
    toRelativeUrl(originalUri || '/okta/login', window.location.origin)
  )
}

function App() {
  const { loading: appLoading } = useAppLoadingStore()
  useDevcycle()
  const { oktaAuth, loading } = useOktaLogin()

  if (loading || appLoading) return <Loading />

  if (oktaAuth) {
    return (
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <Suspense fallback={<Loading />}>
          <RouterProvider router={router} />
          <FreeTierEndedDialog />
        </Suspense>
      </Security>
    )
  }

  return (
    <Suspense fallback={<Loading />}>
      <RouterProvider router={router} />
      <FreeTierEndedDialog />
    </Suspense>
  )
}

const AppWithDevcycle = withDevCycleProvider({
  sdkKey: env.VITE_DEVCYCLE_KEY,
  user: getUser(),
  options: {
    logLevel:
      env.VITE_ENV === 'development' && !window.location.origin.includes('localhost')
        ? 'debug'
        : 'error',
    deferInitialization: getUser() ? false : true
  }
})(App)

export default AppWithDevcycle
